"use client";

import { useEffect, useState } from "react";

import { Toaster as Sonner } from "sonner";

import { useMediaQuery } from "@/hooks/useMediaQuery";
import { tw } from "@/lib/utils/tw";
import { useAuthenticationContext } from "@/services/context/authentication/authentication-context";
import { useTheme } from "@/services/context/theme/theme-context";

type ToasterProps = React.ComponentProps<typeof Sonner>;

const toastBottomClearance = "calc(4rem + env(safe-area-inset-bottom, 0px))";
const toastMobileSideInset =
  "max(16px, env(safe-area-inset-left, 0px), env(safe-area-inset-right, 0px))";
const fineHoverQuery = "(hover: hover) and (pointer: fine)";

function useFineHover() {
  const [fineHover, setFineHover] = useState(
    () => window.matchMedia(fineHoverQuery).matches
  );

  useEffect(() => {
    const media = window.matchMedia(fineHoverQuery);
    const onChange = () => setFineHover(media.matches);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  return fineHover;
}

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme } = useTheme();
  const { isLoggedIn, isLoading } = useAuthenticationContext();
  const md = useMediaQuery("md");
  const fineHover = useFineHover();
  const bottomNavVisible = isLoggedIn && !isLoading && !md;

  return (
    <>
      {/* Sonner's sheet is unlayered (400ms ease, height included). A utility loses to it. Skip an active swipe, which sets transition: none. Reduced motion keeps the fade. */}
      <style>
        {`
          li[data-sonner-toast]:not([data-swiping="true"]) {
            transition:
              transform 200ms cubic-bezier(0.23, 1, 0.32, 1),
              opacity 200ms cubic-bezier(0.23, 1, 0.32, 1) !important;
          }
          @media (prefers-reduced-motion: reduce) {
            li[data-sonner-toast]:not([data-swiping="true"]) {
              transition: opacity 200ms cubic-bezier(0.23, 1, 0.32, 1) !important;
            }
          }
        `}
      </style>
      <Sonner
        theme={theme as ToasterProps["theme"]}
        expand={!fineHover}
        offset={{
          left: "max(24px, env(safe-area-inset-left, 0px))",
          right: "max(24px, env(safe-area-inset-right, 0px))",
          bottom: bottomNavVisible
            ? toastBottomClearance
            : "max(24px, env(safe-area-inset-bottom, 0px))",
        }}
        mobileOffset={{
          left: toastMobileSideInset,
          right: toastMobileSideInset,
          bottom: bottomNavVisible
            ? toastBottomClearance
            : "max(16px, env(safe-area-inset-bottom, 0px))",
        }}
        style={
          {
            "--normal-bg": "var(--background)",
            "--normal-text": "var(--foreground)",
            "--normal-border": "var(--border)",
          } as React.CSSProperties
        }
        toastOptions={{
          classNames: {
            toast: tw`toast group border-border bg-background text-foreground max-mobile:py-2 w-fit border shadow-lg`,
            description: "text-muted-foreground",
            actionButton: tw`actionButton`,
            error: tw`[&_.actionButton]:text-foreground bg-red-100/50! text-red-700! backdrop-blur-3xl! [&_.actionButton]:bg-red-700!`,
          },
        }}
        {...props}
      />
    </>
  );
};

export { Toaster };
